<template>
  <NuxtLayout name="content-only">
    <NuxtLoadingIndicator :height="2" color="#4aa94a" />
    <div class="container mt-9">
      <div class="center p-9">
        <div class="error">
          <div class="number">4</div>
          <div class="illustration">
            <div class="circle"></div>
            <div class="clip">
              <div class="paper">
                <div class="face">
                  <div class="eyes">
                    <div class="eye eye-left"></div>
                    <div class="eye eye-right"></div>
                  </div>
                  <div class="rosyCheeks rosyCheeks-left"></div>
                  <div class="rosyCheeks rosyCheeks-right"></div>
                  <div class="mouth"></div>
                </div>
              </div>
            </div>
          </div>
          <div class="number">4</div>
        </div>

        <p class="text">{{ t('NotFoundPage.description') }}</p>
        <AtomButton
          :action-text="t('NotFoundPage.goToHomePage')"
          button-class="mt-9"
          @click="navigateTo({ path: '/' })"></AtomButton>
        <!-- <AtomOverflowMenu :text="t('Footer.link.changeLang')" position="top">
          <template #list>
            <li v-for="lang in appConfig.availableLocales" :key="lang.name">
              <NuxtLink
                :to="switchLocalePath(lang.code)"
                class="d-flex align-items-center">
                <AtomIcon :icon="lang.flag" icon-size="xs" />
                <span class="ml-6">
                  {{ lang.name }}
                </span>
              </NuxtLink>
            </li>
          </template>
        </AtomOverflowMenu> -->
      </div>
      <div v-if="error && !isProd" class="error-box">
        <h3>Errors:</h3>
        <p><b>URL: </b>"{{ error.url }}"</p>
        <p><b>Status Code: </b>{{ error.statusCode }}</p>
        <p><b>Message: </b>{{ error.message }}</p>
        <p
          ><b>Stack: </b><code> {{ error.stack }}</code></p
        >
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { navigateTo } from '#app';

const { t } = useI18n();
const config = useRuntimeConfig();
const isProd = config.public.isProd;
// const switchLocalePath = useSwitchLocalePath();
// const appConfig = useAppConfig();

interface ErrorObject {
  url?: string;
  statusCode?: number;
  message?: string;
  stack?: string;
}

defineProps({
  error: {
    type: Object as PropType<ErrorObject>,
    default: () => ({})
  }
});

useHead({
  title: t('NotFoundPage.meta.title'),
  meta: [
    {
      name: 'description',
      content: t('NotFoundPage.meta.description')
    }
  ]
});
</script>

<style lang="scss" scoped>
/* colors */
$blue: $color-secondary-500;
$pink: #ccc;
$pink-light: #ccc;
$green: $color-secondary-500;
$green-dark: $color-secondary-500;
$white: white;

a {
  text-decoration: none;
}

.not-found {
  margin: 6rem 12rem;
  @media (max-width: 793px) {
    margin: 6rem 2rem;
  }
}

.center {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.error {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-content: center;
}

.number {
  font-size: 15rem;
  font-weight: font-weight('bold');
  color: $color-secondary-500;
  line-height: 1;
  @media (max-width: 793px) {
    font-size: 10rem;
    font-weight: font-weight('medium');
  }
}

.illustration {
  position: relative;
  width: 12.2rem;
  margin: 0 2.1rem;
  @media (max-width: 793px) {
    margin: 0 0.1rem;
  }
}

.circle {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 12.2rem;
  height: 11.4rem;
  border-radius: 50%;
  background-color: $blue;
  z-index: 1;
}

.clip {
  position: absolute;
  bottom: 0.3rem;
  left: 50%;
  transform: translateX(-50%);
  overflow: hidden;
  width: 12.5rem;
  height: 13rem;
  border-radius: 0 0 50% 50%;
  z-index: 2;
}

.paper {
  position: absolute;
  bottom: -0.3rem;
  left: 50%;
  transform: translateX(-50%);
  width: 9.2rem;
  height: 12.4rem;
  border: 0.3rem solid $blue;
  background-color: $white;
  border-radius: 0.8rem;
}

.face {
  position: relative;
  margin-top: 2.3rem;
}

.eyes {
  position: absolute;
  top: 0;
  left: 2.4rem;
  width: 4.6rem;
  height: 0.8rem;
}

.eye {
  position: absolute;
  bottom: 0;
  width: 0.8rem;
  height: 0.8rem;
  border-radius: 50%;
  background-color: $blue;
  animation-name: eye;
  animation-duration: 4s;
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;

  &-left {
    left: 0;
  }

  &-right {
    right: 0;
  }
}

/* Define the keyframes for the blinking effect */
@-webkit-keyframes eye {
  0%,
  50%,
  100% {
    height: 0.8rem;
  }
  52% {
    height: 0.1rem;
  }
  54% {
    height: 0.8rem;
  }
}

@-moz-keyframes eye {
  0%,
  50%,
  100% {
    height: 0.8rem;
  }
  52% {
    height: 0.1rem;
  }
  54% {
    height: 0.8rem;
  }
}

@keyframes eye {
  0%,
  50%,
  100% {
    height: 0.8rem;
  }
  52% {
    height: 0.1rem;
  }
  54% {
    height: 0.8rem;
  }
}

.eye {
  /* Existing CSS properties */
  animation-name: eye;
  animation-duration: 4s;
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;

  &-left {
    left: 0;
  }

  &-right {
    right: 0;
  }
}

.rosyCheeks {
  position: absolute;
  top: 1.6rem;
  width: 1rem;
  height: 0.2rem;
  border-radius: 50%;
  background-color: $pink;

  &-left {
    left: 1.4rem;
  }

  &-right {
    right: 1.4rem;
  }
}

.mouth {
  position: absolute;
  top: 3.1rem;
  left: 50%;
  width: 1.6rem;
  height: 0.2rem;
  border-radius: 0.1rem;
  transform: translateX(-50%);
  background-color: $blue;
}

.text {
  margin-top: 5rem;
  color: $blue;
  text-align: center;
}

.button {
  margin-top: 4rem;
  padding: 1.2rem 3rem;
  color: $white;
  background-color: $green;

  &:hover {
    background-color: $green-dark;
  }
}

.by {
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  text-transform: uppercase;
  color: $blue;
}

.byLink {
  color: $green;
}

.error-box {
  margin-top: 1rem;
  padding: 1rem;
  background-color: $color-red-100;
  border: 1px solid $color-red-700;
  color: $color-red-700;

  h3 {
    margin-top: 0;
  }
}
</style>
